import React from "react";
import { useAuth } from "@/hooks/use-auth";
import { Link, useLocation } from "wouter";
import { useGetLeadPurchases, getGetLeadPurchasesQueryKey, useDeleteMyAccount } from "@workspace/api-client-react";
import {
  MapPin, Home, Briefcase, Shield, Mail, Edit2,
  Bell, LogOut, Users, Bookmark, Search, Tag, Star,
  TrendingUp, ChevronRight, BarChart2, Receipt, Trash2, ChevronLeft, ShoppingBag,
} from "lucide-react";

export default function Account() {
  const { user, logout } = useAuth();
  const [, setLocation] = useLocation();
  const [avatarImgError, setAvatarImgError] = React.useState(false);

  const { data: leadsData } = useGetLeadPurchases({
    query: { queryKey: getGetLeadPurchasesQueryKey(), enabled: !!user && user.role === "tradesperson" },
  });

  const { mutate: deleteAccount, isPending: deletingAccount } = useDeleteMyAccount({
    mutation: {
      onSuccess: () => {
        logout();
        setLocation("/");
      },
    },
  });

  const handleDeleteAccount = () => {
    if (window.confirm("Are you sure you want to permanently delete your account? This cannot be undone.")) {
      deleteAccount();
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[70vh] bg-slate-50 px-4">
        <div className="w-full max-w-sm bg-white rounded-3xl border border-slate-100 shadow-sm p-7 space-y-5">
          <div className="flex flex-col items-center gap-3">
            <div className="w-20 h-20 rounded-3xl bg-primary/10 flex items-center justify-center">
              <MapPin size={36} className="text-primary" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 text-center">Welcome to Ayrshire Hub</h2>
            <p className="text-sm text-slate-500 text-center leading-relaxed">
              Browse freely — create a free account when you want to post, list a business, or chat in the community boards.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { Icon: Briefcase, text: "List and manage your business" },
              { Icon: Tag, text: "Post deals to local customers" },
              { Icon: ({ size, className }: { size?: number; className?: string }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>, text: "Find quote requests as a tradesperson" },
              { Icon: Users, text: "Chat in your town's community board" },
            ].map(({ Icon, text }, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon size={14} className="text-primary" />
                </div>
                <span className="text-sm text-slate-700">{text}</span>
              </div>
            ))}
          </div>

          <Link href="/signup">
            <button className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-colors">
              Create Free Account
            </button>
          </Link>
          <Link href="/login">
            <button className="w-full py-3.5 border border-slate-200 rounded-2xl text-sm text-slate-600 hover:bg-slate-50 transition-colors">
              Already have an account? <span className="text-primary font-semibold">Sign in</span>
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const isBusiness = user.role === "business";
  const isTrade = user.role === "tradesperson";
  const isResident = user.role === "resident";
  const isAdmin = user.role === "admin";

  const initials = user.name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-xl mx-auto px-4 py-6 space-y-4 pb-16">
        <button onClick={() => window.history.back()} className="flex items-center gap-1 text-sm text-slate-500 hover:text-primary transition-colors -ml-1 mb-1">
          <ChevronLeft size={18} /> Back
        </button>
        <div className="bg-primary rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
              {(user as { avatarUrl?: string | null }).avatarUrl && !avatarImgError ? (
                <img
                  src={(user as { avatarUrl: string }).avatarUrl}
                  alt={user.name}
                  className="w-full h-full rounded-2xl object-cover"
                  onError={() => setAvatarImgError(true)}
                />
              ) : (
                <span className="text-white font-bold text-xl">{initials}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-lg truncate">{user.name}</p>
              <p className="text-white/70 text-sm truncate">{user.email}</p>
              {(user as { town?: string }).town && (
                <div className="flex items-center gap-1 mt-1">
                  <MapPin size={11} className="text-white/60" />
                  <span className="text-white/70 text-xs">{(user as { town: string }).town}</span>
                </div>
              )}
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 bg-white/20 px-3 py-1.5 rounded-full">
            {isBusiness ? <Briefcase size={12} className="text-white" /> : isTrade ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg> : isAdmin ? <Shield size={12} className="text-white" /> : <Home size={12} className="text-white" />}
            <span className="text-white text-xs font-semibold">
              {isBusiness ? "Business Owner" : isTrade ? "Tradesperson" : isAdmin ? "Administrator" : "Resident"}
            </span>
          </div>
          {(user as { bio?: string }).bio && (
            <p className="text-white/80 text-sm leading-relaxed">{(user as { bio: string }).bio}</p>
          )}
        </div>

        {isTrade && leadsData && (
          <div className="grid grid-cols-3 gap-3">
            <StatCard value={leadsData.total} label="Leads Unlocked" color="#1e3a5f" />
            <StatCard value={`£${leadsData.items.reduce((s, l) => s + l.price, 0).toFixed(0)}`} label="Total Spent" color="#f0a500" />
            <StatCard value="4.8★" label="Avg Rating" color="#10b981" />
          </div>
        )}

        {(isBusiness || isAdmin) && (
          <>
            <SectionLabel>MY BUSINESS</SectionLabel>
            <MenuCard>
              <MenuItem Icon={Briefcase} label="List Your Business" sub="Add or manage your business in the directory" accent onClick={() => setLocation("/submit-business")} />
              <MenuItem Icon={Tag} label="Post a Deal" sub="Offer exclusive deals to Ayrshire residents" badge="Paid" onClick={() => alert("Deal posting coming soon.")} />
              <MenuItem Icon={BarChart2} label="Business Dashboard" sub="Views, enquiries and performance" onClick={() => alert("Business dashboard coming soon.")} />
            </MenuCard>
          </>
        )}

        {isTrade && (
          <>
            <SectionLabel>MY TRADE ACCOUNT</SectionLabel>
            <MenuCard>
              <MenuItem Icon={Search} label="Browse Quote Requests" sub="Find work from local residents" accent onClick={() => setLocation("/jobs")} />
              <MenuItem Icon={() => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>} label="Unlocked Leads" sub={`${leadsData?.total ?? 0} leads purchased`} badge="Paid" onClick={() => setLocation("/jobs")} />
              <MenuItem Icon={Star} label="My Reviews" sub="See what customers are saying" onClick={() => alert("Review dashboard coming soon.")} />
            </MenuCard>
          </>
        )}

        {isResident && (
          <>
            <SectionLabel>MY ACTIVITY</SectionLabel>
            <MenuCard>
              <MenuItem Icon={({ size, className }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>} label="Request a Quote" sub="Get quotes from local tradespeople for work around your home" accent onClick={() => setLocation("/post-job")} />
              <MenuItem Icon={({ size, className }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>} label="Submit an Event" sub="Add something to the community calendar" onClick={() => setLocation("/submit-event")} />
            </MenuCard>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-2xl p-4">
                <div className="flex items-center gap-2">
                  <Briefcase size={18} className="text-amber-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-slate-800">Own a business?</p>
                    <p className="text-xs text-slate-500">List it free — add deals &amp; events</p>
                  </div>
                </div>
                <button
                  onClick={() => setLocation("/submit-business")}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-3 py-2 rounded-xl transition-colors"
                >
                  List Business →
                </button>
              </div>

              <div className="flex flex-col gap-3 bg-primary/5 border border-primary/20 rounded-2xl p-4">
                <div className="flex items-center gap-2">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                  <div>
                    <p className="text-sm font-bold text-slate-800">A tradesperson?</p>
                    <p className="text-xs text-slate-500">Find paid job leads nearby</p>
                  </div>
                </div>
                <button
                  onClick={() => setLocation("/become-tradesperson")}
                  className="w-full bg-primary hover:bg-primary/90 text-white text-xs font-bold px-3 py-2 rounded-xl transition-colors"
                >
                  Register Now →
                </button>
              </div>
            </div>
          </>
        )}

        <SectionLabel>MY ORDERS</SectionLabel>
        <MenuCard>
          <MenuItem Icon={ShoppingBag} label="Order History" sub="View your past orders from local shops" onClick={() => setLocation("/my-orders")} />
        </MenuCard>

        <SectionLabel>MY QUOTE REQUESTS</SectionLabel>
        <MenuCard>
          <MenuItem Icon={Briefcase} label="My Quote Requests" sub="View, manage and delete your quote requests" onClick={() => setLocation("/my-jobs")} />
          <MenuItem Icon={({ size, className }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>} label="Post a New Request" sub="Get quotes from local tradespeople" accent onClick={() => setLocation("/post-job")} />
        </MenuCard>

        <SectionLabel>COMMUNITY</SectionLabel>
        <MenuCard>
          <MenuItem Icon={Users} label="Community Boards" sub="Chat with people in your town" onClick={() => setLocation("/community")} />
          <MenuItem Icon={Bookmark} label="Saved Items" sub="News, events and businesses you've saved" onClick={() => setLocation("/saved-items")} />
          <MenuItem Icon={Receipt} label="Transaction History" sub="Your payments and receipts" onClick={() => setLocation("/my-transactions")} />
        </MenuCard>

        <SectionLabel>EARN WITH US</SectionLabel>
        <MenuCard>
          <MenuItem
            Icon={({ size, className }: { size?: number; className?: string }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>}
            label="Influencer Programme"
            sub="Share your code, give 20% off first orders and earn commission for life"
            accent
            onClick={() => setLocation("/account/influencer")}
          />
        </MenuCard>

        <SectionLabel>ACCOUNT</SectionLabel>
        <MenuCard>
          <MenuItem Icon={Edit2} label="Edit Profile" sub="Update your name, photo, and bio" onClick={() => setLocation("/edit-profile")} />
          <MenuItem Icon={Bell} label="Notifications" sub="See your latest alerts and messages" onClick={() => setLocation("/notifications")} />
          <div className="h-px bg-slate-100" />
          <MenuItem Icon={LogOut} label="Log Out" sub="Sign out of your account" destructive onClick={() => { if (window.confirm("Are you sure you want to log out?")) void logout(); }} />
          <div className="h-px bg-slate-100" />
          <MenuItem Icon={Trash2} label={deletingAccount ? "Deleting…" : "Delete Account"} sub="Permanently remove your account and all your data" destructive onClick={handleDeleteAccount} />
        </MenuCard>

        <p className="text-xs text-slate-400 text-center pt-2">Ayrshire Hub v1.0 · Made in Scotland 🏴󠁧󠁢󠁳󠁣󠁴󠁿</p>
        {(user as {isDriver?:boolean}).isDriver && <Link href="/driver-agreement" className="block py-3 text-sm font-medium text-slate-500 underline">View driver agreement</Link>}
      </div>
    </div>
  );
}

function StatCard({ value, label, color }: { value: string | number; label: string; color: string }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col items-center gap-1">
      <span className="text-xl font-bold" style={{ color }}>{value}</span>
      <span className="text-xs text-slate-400 text-center">{label}</span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-1 pt-2">{children}</p>
  );
}

function MenuCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      {children}
    </div>
  );
}

function MenuItem({
  Icon, label, sub, accent, destructive, badge, onClick,
}: {
  Icon: React.FC<{ size?: number; className?: string }>;
  label: string;
  sub: string;
  accent?: boolean;
  destructive?: boolean;
  badge?: string;
  onClick: () => void;
}) {
  const iconColor = destructive ? "#ef4444" : accent ? "#f0a500" : "#1e3a5f";
  const iconBg = destructive ? "#fef2f2" : accent ? "#fffbeb" : "#eff6ff";
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-slate-50 transition-colors"
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: iconBg, color: iconColor }}
      >
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ color: destructive ? "#ef4444" : "#1e293b" }}>{label}</span>
          {badge && (
            <span className="text-[10px] font-bold bg-amber-50 dark:bg-amber-900/20 text-amber-600 px-2 py-0.5 rounded-full">{badge}</span>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
      </div>
      <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
    </button>
  );
}
